import React from 'react';
import { BRAND } from './statuses';
import { RelativeTime } from './RelativeTime';
import type { TicketMessage } from './ticketApi';

interface Props {
  message: TicketMessage;
}

function initial(name: string | null | undefined, fallback: string = '?'): string {
  const n = (name || '').trim();
  if (!n) return fallback;
  return n[0].toUpperCase();
}

export const MessageBubble: React.FC<Props> = ({ message }) => {
  const isInbound = message.direction === 'inbound';
  const isOutbound = message.direction === 'outbound';
  const isNote = message.direction === 'internal_note';
  const isSystem = message.direction === 'system';
  const isDraft = message.direction === 'agent_draft';

  // System messages render as a centered log line
  if (isSystem) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 0',
          fontSize: 11,
          color: BRAND.textMuted,
          gap: 10,
        }}
      >
        <span style={{ flex: 1, height: 1, background: BRAND.borderSoft }} />
        <span>
          {message.body_text} · <RelativeTime iso={message.created_at} />
        </span>
        <span style={{ flex: 1, height: 1, background: BRAND.borderSoft }} />
      </div>
    );
  }

  // Internal note
  if (isNote) {
    return (
      <div style={{ margin: '12px 0', display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            maxWidth: 620,
            width: '100%',
            background: BRAND.noteYellow,
            border: `1px solid ${BRAND.noteYellowBorder}`,
            borderRadius: 10,
            padding: '14px 18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
              fontSize: 11,
              fontWeight: 700,
              color: '#92400E',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            <span>🔒</span>
            <span>Internal note</span>
            <span style={{ color: '#B45309', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
              · {message.author_name || 'Admin'} · <RelativeTime iso={message.created_at} />
            </span>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: '#78350F', whiteSpace: 'pre-wrap' }}>
            {message.body_text}
          </div>
        </div>
      </div>
    );
  }

  // Agent draft — visually distinct unsent reply (reserved for future use)
  if (isDraft) {
    return (
      <div style={{ margin: '12px 0', display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            maxWidth: 620,
            width: '100%',
            background: '#F3F0FF',
            border: '1px dashed #A78BFA',
            borderRadius: 10,
            padding: '14px 18px',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#6D28D9',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 8,
            }}
          >
            🤖 Agent draft · review before sending
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.6, color: '#4C1D95', whiteSpace: 'pre-wrap' }}>
            {message.body_text}
          </div>
        </div>
      </div>
    );
  }

  // Inbound / outbound — side-aligned bubbles
  const authorName = message.author_name || (isInbound ? 'Requester' : 'Support');
  const bg = isOutbound ? BRAND.greenSoft : BRAND.surface;
  const borderColor = isOutbound ? '#C4E3CD' : BRAND.border;
  const avatarBg = isOutbound ? BRAND.green : '#6366F1';

  return (
    <div
      style={{
        margin: '16px 0',
        display: 'flex',
        gap: 12,
        flexDirection: isOutbound ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 100,
          background: avatarBg,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initial(authorName, isInbound ? 'R' : 'A')}
      </div>

      <div style={{ maxWidth: 560, minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 6,
            flexDirection: isOutbound ? 'row-reverse' : 'row',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: BRAND.text }}>
            {authorName}
          </span>
          {message.author_email && (
            <span style={{ fontSize: 11, color: BRAND.textMuted }}>
              {message.author_email}
            </span>
          )}
          <span style={{ fontSize: 11, color: BRAND.textMuted }}>
            · <RelativeTime iso={message.created_at} />
          </span>
        </div>
        <div
          style={{
            background: bg,
            border: `1px solid ${borderColor}`,
            borderRadius: 12,
            padding: '14px 18px',
            fontSize: 14,
            lineHeight: 1.6,
            color: BRAND.textSecondary,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.body_text}
        </div>
        {isOutbound && message.email_delivered === false && (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: '#B91C1C',
              textAlign: 'right',
            }}
          >
            ⚠ Delivery failed: {message.email_delivery_error || 'unknown error'}
          </div>
        )}
        {isOutbound && message.email_delivered && (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: BRAND.textMuted,
              textAlign: 'right',
            }}
          >
            ✓ Delivered via email
          </div>
        )}
      </div>
    </div>
  );
};
